import { useState } from 'react';
import { AuthenticatedLayout } from '@/components/layout/AuthenticatedLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { LeadFormModal } from '@/components/LeadFormModal';
import { Calendar, Users, TrendingUp, Check, Clock } from 'lucide-react';

export const Demo = () => {
  const [showLeadForm, setShowLeadForm] = useState(false);

  const whatWeCover = [
    {
      icon: <Users className="h-6 w-6 text-copper" />,
      title: "Your Workflow",
      description: "See how CampChef Pro adapts to your camp's unique meal planning process"
    },
    {
      icon: <TrendingUp className="h-6 w-6 text-copper" />,
      title: "Cost Savings",
      description: "Real examples of how camps reduce food waste and ordering time"
    },
    {
      icon: <Calendar className="h-6 w-6 text-copper" />,
      title: "Menu Planning",
      description: "Build a sample menu and generate an order in minutes"
    },
    {
      icon: <Clock className="h-6 w-6 text-copper" />,
      title: "Setup & Training",
      description: "Learn about our onboarding process and ongoing support"
    }
  ];

  const testimonials = [
    {
      quote: "The demo showed us exactly how much time we'd save. We were placing orders within a week of signing up!",
      author: "Sarah Johnson",
      role: "Food Service Director, Pine Valley Camp"
    },
    {
      quote: "Being able to see our actual ingredient costs in real-time changed how we plan menus. Game changer.",
      author: "Michael Chen",
      role: "Operations Manager, Lakeside Adventures"
    }
  ];

  const faqs = [
    {
      question: "How long is the demo?",
      answer: "Typically 30-45 minutes. We'll customize it to focus on what matters most to you."
    },
    {
      question: "Do I need to prepare anything?",
      answer: "Just come with your questions! We'll walk through everything step by step."
    },
    {
      question: "Can I get a quote during the demo?",
      answer: "Absolutely! We'll send a custom quote based on your camp size and needs."
    },
    {
      question: "What if my team can't make it?",
      answer: "We can record the session or schedule multiple demos for different team members."
    }
  ];

  return (
    <AuthenticatedLayout>
      <div className="min-h-screen bg-sand">
        {/* Hero Section */}
        <div className="bg-gradient-to-br from-midnight-green to-deep-teal text-white py-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-5xl font-playfair font-bold mb-6">
              See CampChef Pro in Action
            </h1>
            <p className="text-xl text-sand/90 mb-8 max-w-2xl mx-auto">
              Book a personalized demo to see how we can save your camp time and money on meal planning
            </p>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 space-y-16">
          {/* Calendly Embed Placeholder */}
          <Card className="bg-ivory/80 backdrop-blur-sm border-copper/20 shadow-xl">
            <CardHeader className="text-center">
              <CardTitle className="text-3xl font-playfair text-midnight-green">
                Schedule Your Demo
              </CardTitle>
              <CardDescription className="text-deep-teal">
                Choose a time that works best for you
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="bg-white/50 border-2 border-dashed border-copper/30 rounded-lg p-12 text-center">
                <Calendar className="h-16 w-16 text-copper mx-auto mb-4" />
                <p className="text-gray-600 mb-6">
                  Calendly scheduler will be embedded here
                </p>
                <p className="text-sm text-gray-500 mb-6">
                  Or, request a custom quote and we'll reach out to schedule
                </p>
                <Button 
                  onClick={() => setShowLeadForm(true)}
                  className="bg-copper hover:bg-copper/90 text-white"
                  size="lg"
                >
                  Get Pricing Instead
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* What We'll Cover */}
          <div>
            <h2 className="text-3xl font-playfair font-bold text-midnight-green text-center mb-8">
              What We'll Cover
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {whatWeCover.map((item, index) => (
                <Card key={index} className="bg-ivory/80 backdrop-blur-sm border-copper/20 hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="p-3 bg-copper/10 rounded-lg">
                        {item.icon}
                      </div>
                      <div>
                        <h3 className="font-bold text-midnight-green mb-2">{item.title}</h3>
                        <p className="text-sm text-gray-600">{item.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Testimonials */}
          <div>
            <h2 className="text-3xl font-playfair font-bold text-midnight-green text-center mb-8">
              What Our Customers Say
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {testimonials.map((testimonial, index) => (
                <Card key={index} className="bg-ivory/80 backdrop-blur-sm border-copper/20">
                  <CardContent className="p-6">
                    <p className="text-gray-700 italic mb-4">"{testimonial.quote}"</p>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-copper/20 rounded-full" />
                      <div>
                        <p className="font-semibold text-midnight-green">{testimonial.author}</p>
                        <p className="text-sm text-gray-600">{testimonial.role}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* FAQ */}
          <div>
            <h2 className="text-3xl font-playfair font-bold text-midnight-green text-center mb-8">
              Frequently Asked Questions
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {faqs.map((faq, index) => (
                <Card key={index} className="bg-ivory/80 backdrop-blur-sm border-copper/20">
                  <CardContent className="p-6">
                    <h3 className="font-semibold text-midnight-green mb-2 flex items-start gap-2">
                      <Check className="h-5 w-5 text-copper flex-shrink-0 mt-0.5" />
                      {faq.question}
                    </h3>
                    <p className="text-sm text-gray-600 ml-7">{faq.answer}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Bottom CTA */}
          <Card className="bg-gradient-to-br from-midnight-green to-deep-teal text-white border-0 shadow-xl">
            <CardContent className="p-12 text-center">
              <h2 className="text-3xl font-playfair font-bold mb-4">
                Prefer to Get Pricing First?
              </h2>
              <p className="text-sand/90 mb-8 max-w-2xl mx-auto">
                We can send you a custom quote based on your camp size and needs
              </p>
              <Button 
                onClick={() => setShowLeadForm(true)}
                size="lg"
                variant="outline"
                className="bg-white text-midnight-green hover:bg-sand border-0"
              >
                Get Custom Pricing
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>

      <LeadFormModal
        open={showLeadForm}
        onClose={() => setShowLeadForm(false)}
        preselectedPackage="not_sure"
        sourcePage="demo_page"
      />
    </AuthenticatedLayout>
  );
};
